import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBrandName } from '@/i18n/useBrandName'
import { useLogin, useOAuthLogin, useResendVerification, isEmailNotVerifiedError, authFailureKey } from '@/hooks/useAuth'
import { getSafeReturnTo } from '@/lib/safeReturnTo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { BackendHealthIndicator } from '@/components/BackendHealthIndicator'
import { Footer } from '@/components/Footer'
import { cn } from '@/lib/utils'
import { createLogger } from '@/utils/logger'
import { useToast } from '@/hooks/use-toast'

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

/* Microsoft icon — uncomment when Azure AD credentials are configured
function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  )
}
*/

const logger = createLogger('Login');

export default function Login() {
  const { t } = useTranslation()
  const brandName = useBrandName()
  const navigate = useNavigate()
  const login = useLogin()
  const oauthLogin = useOAuthLogin()
  const resend = useResendVerification()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Show error toast if OAuth callback failed
  useEffect(() => {
    const error = searchParams.get('error')
    if (error === 'session_expired') {
      toast({
        title: t('auth.error.sessionExpired'),
        description: t('auth.error.sessionExpiredDesc'),
        variant: 'destructive',
      })
    }
  }, [searchParams, toast, t])

  // VerifyEmail redirects here (rather than /home) when the verification
  // token it just POSTed was a REPLAY of an already-consumed one — the
  // account is genuinely verified but that response minted no session (see
  // AlreadyVerifiedResponse). Not an error — an informational notice.
  useEffect(() => {
    if (searchParams.get('notice') === 'already_verified') {
      toast({
        title: t('auth.notice.alreadyVerified'),
        description: t('auth.notice.alreadyVerifiedDesc'),
      })
    }
  }, [searchParams, toast, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await login.mutateAsync({ email, password })
      // Honour `returnTo` so a user redirected here from another app
      // (care-planner.<env>, the Studio at alg.<env>) lands back where they
      // were, not on /home. Same ORIGIN → SPA navigate to keep router state;
      // any other origin → full-page assign, because react-router cannot leave
      // its origin. Compared by origin, not hostname: in local dev the apps
      // share `localhost` on different ports, and a hostname match sent the
      // Studio's sign-in to the main app's own path instead of back to the
      // Studio. getSafeReturnTo guarantees the URL is parseable and
      // same-host-or-subdomain (open-redirect-safe).
      const returnTo = getSafeReturnTo(searchParams)
      if (returnTo) {
        const url = new URL(returnTo)
        if (url.origin === window.location.origin) {
          void navigate(url.pathname + url.search + url.hash)
        } else {
          window.location.assign(returnTo)
        }
      } else {
        void navigate('/home')
      }
    } catch (err) {
      // Mutation's onError handler shows toast to user
      logger.error('Login submission failed', err)
    }
  }

  const isValid = email.includes('@') && password.length >= 8
  const isLoading = login.isPending || oauthLogin.isPending
  // The login endpoint returns 403 EMAIL_NOT_VERIFIED for an unverified
  // password account — surface a specific, actionable notice instead of the
  // generic "login failed" copy.
  const needsVerification = isEmailNotVerifiedError(login.error)

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col relative overflow-hidden">
      {/* Animated background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary/5 blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-citation/5 blur-3xl translate-y-1/2 -translate-x-1/4" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 relative z-10">
        {/* Logo / Brand */}
        <div className="mb-10 text-center">
          {/* Animated logo with Swiss cross */}
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-pulse-subtle" />
            <div className="absolute inset-1 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center shadow-lg shadow-primary/30">
              <svg viewBox="0 0 24 24" className="w-10 h-10 text-white" fill="currentColor">
                <path d="M19 11h-6V5a1 1 0 0 0-2 0v6H5a1 1 0 0 0 0 2h6v6a1 1 0 0 0 2 0v-6h6a1 1 0 0 0 0-2z" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
            {brandName}
          </h1>
          <p className="mt-2 text-sm text-neutral-500">
            {t('app.tagline')}
          </p>
        </div>

        {/* OAuth buttons */}
        <div className="space-y-3 mb-6">
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={() => oauthLogin.mutate('google')}
            className={cn(
              "w-full h-12 text-base font-medium",
              "bg-white border-neutral-200 hover:bg-neutral-50",
              "transition-colors duration-150",
            )}
          >
            <GoogleIcon className="h-5 w-5 mr-3" />
            {t('auth.continueWithGoogle', 'Continue with Google')}
          </Button>

          {/* Microsoft SSO — hidden until Azure AD credentials are configured
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={() => oauthLogin.mutate('microsoft')}
            className={cn(
              "w-full h-12 text-base font-medium",
              "bg-white border-neutral-200 hover:bg-neutral-50",
              "transition-colors duration-150",
            )}
          >
            <MicrosoftIcon className="h-5 w-5 mr-3" />
            Continue with Microsoft
          </Button>
          */}
        </div>

        {/* Divider */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-neutral-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-neutral-50 px-3 text-neutral-400">
              {t('auth.orEmail', 'or continue with email')}
            </span>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-neutral-700">
              {t('auth.email')}
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder', 'name@hospital.ch')}
              className={cn(
                "h-12 text-base bg-white border-neutral-200",
                "focus:border-primary focus:ring-primary/20",
                "placeholder:text-neutral-400"
              )}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium text-neutral-700">
              {t('auth.password')}
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordDotsPlaceholder', '••••••••')}
              className={cn(
                "h-12 text-base bg-white border-neutral-200",
                "focus:border-primary focus:ring-primary/20",
                "placeholder:text-neutral-400"
              )}
            />
          </div>

          {needsVerification ? (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
              <p className="text-sm font-medium text-neutral-900">
                {t('auth.notVerified.title')}
              </p>
              <p className="text-sm text-neutral-600">
                {t('auth.notVerified.description')}
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => resend.mutate(email)}
                disabled={resend.isPending || !email.includes('@')}
                className={cn(
                  "w-full h-11 text-sm font-medium",
                  "bg-white border-primary/30 text-primary hover:bg-primary/5",
                  "transition-colors duration-150",
                  "disabled:opacity-50"
                )}
              >
                {resend.isPending ? (
                  <span className="flex items-center gap-2">
                    <Spinner className="h-4 w-4" />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.notVerified.resend')
                )}
              </Button>
              {resend.isSuccess && !resend.isPending && (
                <p className="text-sm text-primary" role="status">
                  {t('auth.verify.inbox.resent')}
                </p>
              )}
              {resend.isError && !resend.isPending && (
                <p className="text-sm text-error" role="alert">
                  {t('auth.verify.resendFailed')}
                </p>
              )}
            </div>
          ) : login.error ? (
            <div className="p-3 rounded-lg bg-error/10 border border-error/20">
              <p className="text-sm text-error">
                {/* Only a real rejection may claim the credentials are wrong.
                    A request that never reached the server (dead Wi-Fi, VPN
                    flap, timeout) or one our own 5xx killed carries NO verdict
                    on the password — telling the nurse to check it sends her
                    to re-type something that was never the problem, and hides
                    the layer that actually broke. */}
                {t(authFailureKey(login.error, 'auth.loginFailed'))}
              </p>
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={!isValid || isLoading}
            className={cn(
              "w-full h-12 text-base font-medium mt-4",
              "bg-primary hover:bg-primary-600 active:bg-primary-700",
              "transition-colors duration-150",
              "disabled:opacity-50"
            )}
          >
            {login.isPending ? (
              <span className="flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                {t('common.loading')}
              </span>
            ) : (
              t('auth.login')
            )}
          </Button>
        </form>

        {/* Register link */}
        <p className="mt-8 text-center text-sm text-neutral-500">
          {t('auth.noAccount')}{' '}
          <Link
            to="/register"
            className="font-medium text-primary hover:text-primary-600 transition-colors"
          >
            {t('auth.register')}
          </Link>
        </p>

        {/* Backend health indicator */}
        <BackendHealthIndicator className="mt-6" />
      </div>

      <Footer />

      {/* Safe area spacer for iOS */}
      <div className="h-safe-area-inset-bottom" />
    </div>
  )
}
